<template>
  <div class="packing-list f-c" v-loading="loadingData">
    <div class="header-buttons" v-if="showHeaderOps">
      <el-button
        type="primary"
        plain
        style="width: 100px"
        size="mini"
        @click="toggleTableExpand = !toggleTableExpand"
        >{{ toggleTableExpand ? 'Show' : 'Hide' }} Detail
      </el-button>
    </div>
    <basic-table
      :table-data="pageData"
      :table-headers="GoodsTableHeaders"
      :has-actions="showActions"
      :has-row-expand="true"
      :action-min-width="180"
      :sortChange="sortChange"
      :checkExpand="checkExpand"
      :paginationChange="paginationChange"
      :showPagination="showPagination"
      :rowClassName="tableRowClass"
      :summary="true"
      :stripe="stripe"
    >
      <template v-slot:row-expand="props">
        <div class="note-list f-r">
          <div
            v-for="(note, index) in props.row.notes"
            :key="'note-' + index"
            class="note f-c"
            :class="{
              important: note.status === NoteImportant,
              unimportant: note.status === NoteDeleted
            }"
          >
            <div class="note-content">
              <div class="time">
                {{ getNoteCreatedTime(note) }}
              </div>
              <div
                class="content"
                :class="{
                  striked: note.status === NoteDeleted
                }"
              >
                {{ note.content }}
              </div>
            </div>
            <div class="status-buts f-r">
              <svg-icon
                icon-class="important"
                @click.native="
                  () =>
                    changeStatus(
                      props.$index,
                      props.row,
                      index,
                      note,
                      NoteNormal
                    )
                "
                v-if="note.status === NoteImportant"
              />
              <svg-icon
                icon-class="unimportant"
                @click.native="
                  () =>
                    changeStatus(
                      props.$index,
                      props.row,
                      index,
                      note,
                      NoteImportant
                    )
                "
                v-if="note.status === NoteNormal"
              />
              <svg-icon
                icon-class="trashed"
                @click.native="
                  () =>
                    changeStatus(
                      props.$index,
                      props.row,
                      index,
                      note,
                      note.status === NoteDeleted ? NoteNormal : NoteDeleted
                    )
                "
                :class="{ deleted: note.status === NoteDeleted }"
              />
            </div>
          </div>
        </div>
      </template>
      <template v-slot:row-actions="props">
        <slot
          name="actions"
          v-bind:row="props.row"
          v-bind:index="props.index"
        />
      </template>
      <template v-slot:footer-ops>
        <el-button
          type="primary"
          plain
          style="width: 100px"
          size="mini"
          @click="toggleTableExpand = !toggleTableExpand"
          >{{ toggleTableExpand ? 'Show' : 'Hide' }} Detail
        </el-button>
      </template>
    </basic-table>
  </div>
</template>
<script>
import cloneDeep from 'lodash/cloneDeep'
import isEmpty from 'lodash/isEmpty'
// types
import {
  Ascending,
  DefaultPageSize,
  Descending,
  getDefaultSort,
  PageCurrentKey,
  PageSizeKey
} from '@/core/configs/types'
// msg
import PagesMessage from '@/core/message/pages'
// api
import { modifyGoodsNotes, queryGoods } from '@/api/goods'
// models
import { GoodsTableHeaders } from '@/core/models/goods'
import { NoteDeleted, NoteNormal, NoteImportant } from '@/core/models/note'
// components
import BasicTable from '@/components/common/BasicTable.vue'
export default {
  name: 'PackingList',
  components: { BasicTable },
  props: {
    queryParams: {
      type: Object,
      default: () => {}
    },
    goods: {
      type: Array,
      default: () => []
    },
    showActions: {
      type: Boolean,
      default: true
    },
    showAddressHint: {
      type: Boolean,
      default: false
    },
    showHeaderOps: {
      type: Boolean,
      default: true
    },
    tableRowClass: {
      type: Function,
      default: () => {}
    },
    stripe: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      GoodsTableHeaders,
      NoteDeleted,
      NoteNormal,
      NoteImportant,
      goodsList: [],
      pagination: {
        [PageSizeKey]: DefaultPageSize,
        [PageCurrentKey]: 1,
        total: 0,
        asc: '',
        desc: ''
      },
      loadingData: false,
      toggleTableExpand: false
    }
  },
  computed: {
    pageData() {
      return {
        records: this.goodsList,
        ...this.pagination
      }
    },
    showPagination() {
      return this.goods.length === 0
    }
  },
  watch: {
    queryParams: {
      handler() {
        if (!isEmpty(this.queryParams)) {
          this.fetchGoodsData()
        }
      },
      immediate: true
    },
    goods: {
      handler() {
        this.goodsList = this.goods
      },
      immediate: true
    }
  },
  methods: {
    sortChange(sort) {
      if (sort.order === Ascending) {
        this.pagination.asc = sort.col
        this.pagination.desc = ''
      } else if (sort.order === Descending) {
        this.pagination.desc = sort.col
        this.pagination.asc = ''
      }
      this.fetchGoodsData()
    },
    paginationChange(key, path, value) {
      this.pagination[key] = value
      this.fetchGoodsData()
    },
    async fetchGoodsData() {
      if (!isEmpty(this.queryParams)) {
        getDefaultSort(GoodsTableHeaders, this.pagination)
        this.loadingData = true
        const page = await queryGoods({
          ...this.queryParams,
          ...this.pagination
        })
        this.$set(this, 'goodsList', page?.records ?? [])
        this.pagination.total = page.total
        this.loadingData = false
      }
    },
    getNoteCreatedTime(note) {
      const date = new Date(note.createdTime)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
    },
    changeStatus(index, goods, noteIndex, note, status) {
      const original = cloneDeep(goods.notes)
      original[noteIndex].status = status
      modifyGoodsNotes(goods.id, original).then(goods => {
        if (goods) {
          PagesMessage.emit(PagesMessage.eventMap.REFRESH_ORDER_DETAIL)
        }
      })
    },
    checkExpand(item) {
      return !this.toggleTableExpand && item?.notes?.length > 0
    }
  }
}
</script>
<style scoped lang="scss">
@import '@/assets/styles/common.scss';
@import '@/assets/styles/note-list.scss';

.table-header {
  padding-bottom: 10px;
}

.header-buttons {
  margin-bottom: 10px;
}

.error-info {
  @extend .common-hint-list;
}
</style>
